import { Quote, MapPin } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { TESTIMONIALS, LOCATIONS } from "@/lib/data";
import { useI18n } from "@/context/LanguageContext";

const MAP_SRC = "https://www.google.com/maps?q=21.140579,79.0931014&z=17&output=embed";

export const Trust = () => {
  const { t } = useI18n();
  const tr = t.trust;
  return (
    <section id="trust" className="bg-offwhite py-16 sm:py-20 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionLabel index="/ 05">{t.labels.trust}</SectionLabel>
          <h2 className="font-display font-700 uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-charcoal max-w-2xl mb-14">
            {tr.heading[0]} <span className="text-safety">{tr.heading[1]}</span>
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-5 mb-16">
          <Reveal className="lg:col-span-7">
            <div className="relative border-2 border-charcoal h-[380px] overflow-hidden hard-shadow-sm">
              <iframe
                title="Nidhi Fabrication Workshop Location"
                src={MAP_SRC}
                className="w-full h-full grayscale contrast-[1.1]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="location-map"
              />
              <div className="absolute top-3 left-3 bg-safety text-white font-mono2 text-[10px] font-700 uppercase tracking-wider px-3 py-1.5">
                {tr.mapPin}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="bg-charcoal text-white border-2 border-charcoal h-full p-8 flex flex-col justify-between">
              <div>
                <h3 className="font-display font-700 uppercase text-2xl mb-6">{tr.coverage}</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  {LOCATIONS.map((l) => (
                    <div key={l} className="flex items-center gap-2.5 border-b border-white/10 pb-3">
                      <MapPin size={15} className="text-safety flex-shrink-0" />
                      <span className="font-500 text-sm">{l}</span>
                    </div>
                  ))}
                </div>
              </div>
              <p className="font-mono2 text-xs text-white/40 tracking-wide mt-8">{tr.coverageNote}</p>
            </div>
          </Reveal>
        </div>

        {/* Made in India trust strip */}
        <Reveal>
          <div data-testid="india-trust-strip" className="bg-charcoal text-white border-2 border-charcoal mb-16 grid md:grid-cols-[auto_1fr] items-stretch">
            <div className="flex items-center gap-4 p-7 border-b-2 md:border-b-0 md:border-r-2 border-white/10">
              <span className="flex h-9 w-14 flex-col overflow-hidden rounded-[2px] border border-white/30 flex-shrink-0">
                <span className="flex-1 bg-[#FF9933]" />
                <span className="flex-1 bg-white" />
                <span className="flex-1 bg-[#138808]" />
              </span>
              <div>
                <div className="font-display font-700 uppercase text-xl leading-none">{tr.madeInIndia}</div>
                <div className="font-deva text-safety text-sm mt-1">आत्मनिर्भर भारत</div>
              </div>
            </div>
            <div className="p-7 flex flex-col justify-center gap-4">
              <div className="flex flex-wrap gap-2.5">
                {["GST Registered", "MSME Udyam", "ISO 9001:2015", "IS 800 / 2062"].map((b) => (
                  <span key={b} className="font-mono2 text-[11px] font-700 uppercase tracking-wider px-3 py-1.5 border border-safety text-safety">
                    {b}
                  </span>
                ))}
              </div>
              <p className="font-mono2 text-xs text-white/50 tracking-wide">
                // {tr.since} · {tr.pricing}
              </p>
            </div>
          </div>
        </Reveal>

        {/* Testimonials */}
        <div className="grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t2, i) => (
            <Reveal key={t2.name} delay={i * 0.1}>
              <div data-testid={`testimonial-${i}`} className="bg-white border-2 border-charcoal p-7 h-full flex flex-col hover:-translate-y-1 transition-transform duration-200">
                <Quote size={30} className="text-safety mb-5" />
                <p className="text-charcoal/80 text-[15px] leading-relaxed flex-1">"{tr.testimonials[i].quote}"</p>
                <div className="mt-6 pt-5 border-t-2 border-charcoal/10 flex items-center gap-3">
                  <div className="w-11 h-11 stripes flex items-center justify-center border-2 border-charcoal">
                    <span className="font-display font-700 text-white mix-blend-difference">
                      {t2.name.split(" ").map((n) => n[0]).join("")}
                    </span>
                  </div>
                  <div>
                    <div className="font-display font-700 uppercase text-charcoal leading-none">{t2.name}</div>
                    <div className="font-mono2 text-[10px] text-charcoal/50 mt-1 tracking-wide">
                      {tr.testimonials[i].role} · {t2.place}
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
